import Checkbox from "app/components/Checklists/Checkbox";
import Select from "react-select";
import SimpleModalFooter from "app/components/Modals/SimpleModalFooter";
import { useEffect, useState } from "react";
import { useAppSelector } from "utils/redux/hooks";
import { selectDepartments } from "app/containers/AdminConsole/slice";
import { SendEmailOptions } from "../types";
import { getMustSelectUserRoleErrorBanner } from "../helpers";

interface Props {
  onBack: () => void;
  onSave: (
    selectedDepartmentIds: number[],
    selectedOptionValues?: SendEmailOptions
  ) => void;
  preselectedUserIds?: number[];
  sendEmailOptions: SendEmailOptions;
  getWarningBanner?: (sendToType: string) => JSX.Element | null;
}

const DepartmentEmailConfig = ({
  onBack,
  onSave,
  preselectedUserIds,
  sendEmailOptions,
  getWarningBanner,
}: Props) => {
  const departments = useAppSelector(selectDepartments);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [areTeamLeadersSelected, setAreTeamLeadersSelected] = useState(false);
  const [areTeamMembersSelected, setAreTeamMembersSelected] = useState(false);
  const [areDepartmentLeadersSelected, setAreDepartmentLeadersSelected] =
    useState(false);
  const [showMustSelectCheckboxError, setShowMustSelectCheckboxError] =
    useState(false);

  useEffect(() => {
    setAreTeamLeadersSelected(!!sendEmailOptions.teamLeaders);
    setAreTeamMembersSelected(!!sendEmailOptions.teamMembers);
    setAreDepartmentLeadersSelected(!!sendEmailOptions.departmentLeaders);
  }, [sendEmailOptions]);

  useEffect(() => {
    if (preselectedUserIds) {
      setSelectedIds(preselectedUserIds);
    }
  }, [preselectedUserIds]);

  const onCheckboxChangeSideEffect = () => {
    setShowMustSelectCheckboxError(false);
  };

  return (
    <>
      <Select
        noOptionsMessage={() => null}
        placeholder="Search by department name"
        options={Object.values(departments)
          .filter((department) => !!department.companyAccountId)
          .map((department) => ({
            label: department.name ?? "",
            value: `${department.departmentId}`,
          }))}
        isMulti
        value={selectedIds.map((id) => ({
          label: departments[id]?.name,
          value: String(departments[id]?.departmentId),
        }))}
        onChange={(values) => {
          if (values) {
            setSelectedIds(values.map(({ value }) => Number(value)));
          }
        }}
        classNames={{
          multiValue: () => "select__multi-value",
        }}
      />
      <div className="column-gap-8px">
        <p className="navy-text">
          In departments selected above, send this email to:
        </p>
        <Checkbox
          id="departmentLeaders"
          label="Department Leaders"
          onChange={() => {
            onCheckboxChangeSideEffect();
            setAreDepartmentLeadersSelected(!areDepartmentLeadersSelected);
          }}
          value={areDepartmentLeadersSelected}
        />
        <Checkbox
          id="teamLeaders"
          label="Team Leaders"
          onChange={() => {
            onCheckboxChangeSideEffect();
            setAreTeamLeadersSelected(!areTeamLeadersSelected);
          }}
          value={areTeamLeadersSelected}
        />
        <Checkbox
          id="teamMembers"
          label="Team Members"
          onChange={() => {
            onCheckboxChangeSideEffect();
            setAreTeamMembersSelected(!areTeamMembersSelected);
          }}
          value={areTeamMembersSelected}
        />
      </div>
      {selectedIds?.length && getWarningBanner
        ? getWarningBanner("departments")
        : null}
      {showMustSelectCheckboxError && getMustSelectUserRoleErrorBanner()}
      <SimpleModalFooter
        onBackButtonClick={onBack}
        onPrimaryButtonClick={() => {
          if (
            !areDepartmentLeadersSelected &&
            !areTeamLeadersSelected &&
            !areTeamMembersSelected
          ) {
            return setShowMustSelectCheckboxError(true);
          }

          onSave(selectedIds, {
            departmentLeaders: areDepartmentLeadersSelected,
            teamLeaders: areTeamLeadersSelected,
            teamMembers: areTeamMembersSelected,
          });
        }}
        isPrimaryButtonDisabled={
          !selectedIds.length || showMustSelectCheckboxError
        }
        primaryButtonText="Next"
      />
    </>
  );
};

export default DepartmentEmailConfig;
